import {
  type OnChangeFn,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
  functionalUpdate,
  makeStateUpdater,
} from "@tanstack/react-table";

export interface RowFocusTableState {
  focusedRowId?: string;
}

export interface RowFocusOptions {
  enableRowFocus?: boolean;
  onRowFocusChange?: OnChangeFn<string | undefined>;
}

export interface RowFocusInstance {
  setFocusedRow: (updater: Updater<string | undefined>) => void;
  getFocusedRowId: () => string | undefined;
}

declare module "@tanstack/react-table" {
  interface TableState extends RowFocusTableState {}
  interface TableOptionsResolved<TData extends RowData>
    extends RowFocusOptions {}
  interface Table<TData extends RowData> extends RowFocusInstance {}
}

export const RowFocusFeature: TableFeature<any> = {
  getInitialState: (state): RowFocusTableState => {
    return {
      focusedRowId: undefined,
      ...state,
    };
  },
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): RowFocusOptions => {
    return {
      enableRowFocus: true,
      onRowFocusChange: makeStateUpdater("focusedRowId", table),
    } as RowFocusOptions;
  },
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.setFocusedRow = (updater) => {
      const safeUpdater: Updater<string | undefined> = (old) => {
        const newState = functionalUpdate(updater, old);
        return newState;
      };
      return table.options.onRowFocusChange?.(safeUpdater);
    };
    table.getFocusedRowId = () => {
      return table.getState().focusedRowId;
    };
  },
};
